import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, Minus, Plus, Zap, Mic, BookOpen, ArrowRightLeft, Users, Video,
  CalendarClock, Truck, Stethoscope, RotateCcw, ChevronRight,
  Menu, X, Sparkles, ShieldCheck,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { ServisfyLogo } from '../Common/ServisfyLogo';
import LanguageSelector from '../Common/LanguageSelector';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchPublicPricing } from '../../lib/pricingApi';

/* DISPLAY METADATA ONLY. All user-facing copy resolves through t('pr.*') (see pricingTranslations.js).
 * Brand/product proper nouns, plan names (from the catalog), prices, numbers, units and meter ids are
 * never translated. The detailed industry bullet-lists below are the flagged long marketing copy
 * (NATIVE_REVIEW_PENDING) and render from English source until the per-language QA pass lands. */
const SALES_EMAIL = 'sales@servisfy.com';
const FEATURE_LABEL = {
  ticketing: 'Ticketing', customer_management: 'Contact management', staff_management: 'Staff & operator management',
  solution_bank: 'SolutionBank knowledge base', ai_chat: 'AI Chat', ai_voice: 'AI Voice', solution_bank_advanced: 'SolutionBank Advanced',
  escalations: 'Human escalation & handoff', escalations_callbacks: 'Human escalation & callbacks', operator_supervision: 'Operator supervision',
  whisper: 'Whisper coaching', join_call: 'Join / barge call', sla: 'SLA management', sla_management: 'SLA management', surveys: 'Surveys',
  scheduling: 'Scheduling & consultations', workflow: 'Workflow automation', workflow_automation: 'Workflow automation',
  advanced_analytics: 'Advanced analytics & reporting', api_access: 'API access', multi_location: 'Multi-location',
  sso_saml: 'SSO / SAML', white_label: 'White-label', data_residency: 'Data residency',
};
const LIMIT_LABEL = { max_staff: 'Staff', monthly_engagements: 'Engagements / mo', monthly_ai_resolutions: 'AI resolutions / mo', storage_gb: 'Storage (GB)', active_integrations: 'Integration slots' };
// 10 comparison groups — group name via t(gKey); rows are feature keys, {limit}, or {tag} markers.
const COMPARE_GROUPS = [
  { gKey: 'pr.cmp.g1', rows: ['ai_chat', 'ai_voice', 'solution_bank', 'solution_bank_advanced'] },
  { gKey: 'pr.cmp.g2', rows: ['escalations_callbacks', 'surveys'] },
  { gKey: 'pr.cmp.g3', rows: ['operator_supervision', 'whisper', 'join_call'] },
  { gKey: 'pr.cmp.g4', rows: ['scheduling', 'workflow_automation', { tag: 'add', labelKey: 'pr.cmp.r.schedpacks' }] },
  { gKey: 'pr.cmp.g5', rows: [{ tag: 'ind', labelKey: 'pr.cmp.r.crew' }, { tag: 'ind', labelKey: 'pr.cmp.r.dispatch' }] },
  { gKey: 'pr.cmp.g6', rows: [{ tag: 'ind', labelKey: 'pr.cmp.r.gps' }, { tag: 'ind', labelKey: 'pr.cmp.r.eta' }, { tag: 'usage', labelKey: 'pr.cmp.r.smslinks' }] },
  { gKey: 'pr.cmp.g7', rows: [{ tag: 'ind', labelKey: 'pr.cmp.r.audio' }, { tag: 'ind', labelKey: 'pr.cmp.r.video' }] },
  { gKey: 'pr.cmp.g8', rows: [{ tag: 'usage', labelKey: 'pr.cmp.r.trans' }, { tag: 'usage', labelKey: 'pr.cmp.r.aidoc' }] },
  { gKey: 'pr.cmp.g9', rows: ['advanced_analytics', 'api_access', { limit: 'storage_gb' }, { limit: 'active_integrations' }] },
  { gKey: 'pr.cmp.g10', rows: [{ limit: 'max_staff' }, { limit: 'monthly_engagements' }, { limit: 'monthly_ai_resolutions' }] },
];
const SHARED = [
  { icon: Zap, t: 'AI Chat', d: 'pr.cap.aichat.d' }, { icon: Mic, t: 'AI Voice', d: 'pr.cap.aivoice.d' },
  { icon: BookOpen, t: 'SolutionBank', d: 'pr.cap.sb.d' }, { icon: ArrowRightLeft, tk: 'pr.cap.esc.t', d: 'pr.cap.esc.d' },
  { icon: Users, tk: 'pr.cap.oper.t', d: 'pr.cap.oper.d' }, { icon: Video, tk: 'pr.cap.team.t', d: 'pr.cap.team.d' },
  { icon: CalendarClock, tk: 'pr.cap.sched.t', d: 'pr.cap.sched.d' }, { icon: ShieldCheck, tk: 'pr.cap.gov.t', d: 'pr.cap.gov.d' },
];
const TRANSPORT_JOURNEY = ['pr.journey.request', 'pr.journey.schedule', 'pr.journey.assign', 'pr.journey.dispatch', 'pr.journey.track', 'pr.journey.arrive', 'pr.journey.complete'];
const HEALTH_JOURNEY = ['pr.journey.book', 'pr.journey.consult', 'pr.journey.communicate', 'pr.journey.document', 'pr.journey.review', 'pr.journey.followup'];
// Detailed capability bullets (flagged long copy — English source pending native QA).
const TRANSPORT_GROUPS = [
  { hKey: 'pr.tr.g1.h', itemsKey: 'pr.tr.g1.items' },
  { hKey: 'pr.tr.g2.h', itemsKey: 'pr.tr.g2.items' },
  { hKey: 'pr.tr.g3.h', itemsKey: 'pr.tr.g3.items' },
];
const HEALTH_GROUPS = [
  { hKey: 'pr.he.g1.h', itemsKey: 'pr.he.g1.items' },
  { hKey: 'pr.he.g2.h', itemsKey: 'pr.he.g2.items' },
  { hKey: 'pr.he.g3.h', itemsKey: 'pr.he.g3.items' },
];
const CLINICAL_METERS = [
  { nameKey: 'pr.meter.audio', rateKey: 'pr.meter.credit1', perKey: 'pr.meter.audioPer', bucketKey: 'pr.meter.commBucket', key: 'consultation_audio_minute' },
  { nameKey: 'pr.meter.video', rateKey: 'pr.meter.credit2', perKey: 'pr.meter.videoPer', bucketKey: 'pr.meter.commBucket', key: 'consultation_video_minute' },
  { nameKey: 'pr.meter.trans', rateKey: 'pr.meter.credit1', perKey: 'pr.meter.transPer', bucketKey: 'pr.meter.aiBucket', key: 'transcription_minute' },
  { nameKey: 'pr.meter.aidoc', rateKey: 'pr.meter.credit10', perKey: 'pr.meter.aidocPer', bucketKey: 'pr.meter.aiBucket', key: 'ai_documentation_generation' },
];
const MOVERS_METERS = [
  { nameKey: 'pr.meter.sms', rate: '$0.05', perKey: 'pr.meter.smsPer', key: 'tracking_sms_sent' },
  { nameKey: 'pr.meter.routes', rate: '$0.01', perKey: 'pr.meter.routesPer', key: 'google_routes_compute' },
];
const PLAN_FIT = [
  { whoKey: 'pr.fit.who1', plan: 'starter', whyKey: 'pr.fit.why1' },
  { whoKey: 'pr.fit.who2', plan: 'growth', whyKey: 'pr.fit.why2' },
  { whoKey: 'pr.fit.who3', plan: 'professional', whyKey: 'pr.fit.why3' },
];
const FAQ_KEYS = [['pr.faq.q1', 'pr.faq.a1'], ['pr.faq.q2', 'pr.faq.a2'], ['pr.faq.q3', 'pr.faq.a3'], ['pr.faq.q4', 'pr.faq.a4'], ['pr.faq.q5', 'pr.faq.a5'], ['pr.faq.q6', 'pr.faq.a6']];

const money = (v) => (v == null ? '—' : '$' + Number(v).toLocaleString('en-US'));
const lim = (v, unlimited) => (v === -1 ? unlimited : v == null ? '—' : Number(v).toLocaleString('en-US'));
const GRAD = 'bg-brand-gradient';
const GRAD_HOVER = 'hover:brightness-110';
const GRAD_TEXT = 'text-brand-gradient';
// Translated label resolvers. Prefer the per-language pr.feat.* / pr.lim.* keys; if a server
// capability key has no translation entry, fall back to the English metadata map, then the raw key.
const flabel = (f, t) => { const k = 'pr.feat.' + f; const v = t(k); return v === k ? (FEATURE_LABEL[f] || f) : v; };
const llabel = (k, t) => { const key = 'pr.lim.' + k; const v = t(key); return v === key ? (LIMIT_LABEL[k] || k) : v; };

export default function PricingPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true); setError(false);
    fetchPublicPricing(ctrl.signal)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { if (e.name !== 'AbortError') { setError(true); setLoading(false); } });
    return () => ctrl.abort();
  }, []);

  const plans = data?.plans || [];
  const addOns = data?.add_ons || [];
  const usagePacks = data?.usage_packs || [];
  const recurringAddOns = addOns.filter((a) => !a.industry_gated);
  const industryAddOns = addOns.filter((a) => a.industry_gated);
  const ladder = plans.filter((p) => !p.is_trial);
  const go = (id) => { setMobileNav(false); const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); };
  const NAV = [['plans', 'pr.nav.plans'], ['platform', 'pr.nav.platform'], ['transport', 'pr.nav.transport'], ['health', 'pr.nav.health'], ['compare', 'pr.nav.compare'], ['estimator', 'pr.nav.estimator'], ['addons', 'pr.nav.addons'], ['faq', 'pr.nav.faq']];

  return (
    <div className="servisfy-brand min-h-screen text-foreground bg-background bg-gradient-to-br from-background via-secondary/40 to-accent/40" style={{ backgroundAttachment: 'fixed' }}>
      <nav className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-5 h-20 flex items-center gap-6">
          <button onClick={() => navigate('/login')} aria-label="Servisfy" className="shrink-0 flex items-center">
            <ServisfyLogo variant="full" size="xl" />
          </button>
          <div className="hidden lg:flex gap-5 text-sm font-medium text-muted-foreground">
            {NAV.map(([id, k]) => <button key={id} className="hover:text-primary" onClick={() => go(id)}>{t(k)}</button>)}
            <button className="hover:text-primary" onClick={() => navigate('/partners')}>Partner Program</button>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block w-40"><LanguageSelector /></div>
            <Button variant="outline" className="h-9 hidden sm:inline-flex" onClick={() => navigate('/login')}>{t('pr.nav.login')}</Button>
            <Button className={`h-9 text-primary-foreground ${GRAD} ${GRAD_HOVER}`} onClick={() => navigate('/signup')}>{t('pr.nav.signup')}</Button>
            <button className="lg:hidden p-2" aria-label="Menu" onClick={() => setMobileNav((v) => !v)}>{mobileNav ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
          </div>
        </div>
        {mobileNav && (
          <div className="lg:hidden border-t border-border bg-card px-5 py-3 flex flex-col gap-3 text-sm font-medium">
            {NAV.map(([id, k]) => <button key={id} className="text-left py-1 hover:text-primary" onClick={() => go(id)}>{t(k)}</button>)}
            <button className="text-left py-1 hover:text-primary" onClick={() => navigate('/partners')}>Partner Program</button>
            <div className="pt-1 w-full"><LanguageSelector /></div>
          </div>
        )}
      </nav>

      <header className="mx-auto max-w-6xl px-5 pt-16 pb-10 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-4 py-1.5 text-sm font-semibold text-primary shadow-sm">
          <Sparkles className="h-4 w-4" /> {t('pr.hero.badge')}
        </span>
        <h1 className="mt-6 mb-4 mx-auto max-w-3xl text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08] text-foreground">
          {t('pr.hero.titleA')} <span className={GRAD_TEXT}>{t('pr.hero.titleB')}</span>
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-muted-foreground mb-7">{t('pr.hero.subtitle')}</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Button className={`h-11 px-6 text-primary-foreground ${GRAD} ${GRAD_HOVER}`} onClick={() => go('plans')}>{t('pr.hero.ctaPlans')}</Button>
          <Button variant="outline" className="h-11 px-6" onClick={() => go('estimator')}>{t('pr.hero.ctaEstimate')}</Button>
        </div>
        <div className="flex gap-2.5 justify-center flex-wrap mt-8">
          {['pr.hero.chipTrial', 'pr.hero.chipMonthly', 'pr.hero.chipTenant', 'pr.hero.chipPlatforms'].map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5 rounded-full bg-card/70 border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-success" /> {t(k)}
            </span>
          ))}
        </div>
      </header>

      {loading && <SectionState>{t('pr.state.loading')}</SectionState>}
      {error && (
        <SectionState>
          <p className="font-semibold text-foreground">{t('pr.state.errorTitle')}</p>
          <p className="text-muted-foreground text-sm mt-1">{t('pr.state.errorBody')} <button className="text-primary font-semibold underline" onClick={() => window.location.reload()}>{t('pr.state.tryAgain')}</button>.</p>
        </SectionState>
      )}

      {!loading && !error && data && (
        <>
          <Section id="plans" eyebrow={t('pr.plans.eyebrow')} title={t('pr.plans.title')} subtitle={t('pr.plans.subtitle')}>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {plans.map((p) => <PlanCard key={p.plan_id} p={p} navigate={navigate} t={t} />)}
            </div>
            <p className="text-center text-sm text-muted-foreground mt-6">{t('pr.plans.footnote')}</p>
          </Section>

          <div className="bg-card/40">
            <Section id="platform" eyebrow={t('pr.platform.eyebrow')} title={t('pr.platform.title')} subtitle={t('pr.platform.subtitle')}>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {SHARED.map((s) => (
                  <div key={s.d} className="rounded-xl bg-card border border-border p-5 shadow-[0_10px_30px_-22px_rgba(0,0,0,0.3)]">
                    <div className={`h-11 w-11 rounded-xl ${GRAD} text-primary-foreground flex items-center justify-center mb-3`}><s.icon className="h-5 w-5" /></div>
                    <h4 className="font-semibold text-[15px] mb-1">{s.tk ? t(s.tk) : s.t}</h4>
                    <p className="text-sm text-muted-foreground">{t(s.d)}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-6"><button className="text-sm font-bold text-primary inline-flex items-center gap-1" onClick={() => go('compare')}>{t('pr.platform.link')} <ChevronRight className="h-4 w-4" /></button></div>
            </Section>
          </div>

          <Section id="transport" eyebrow={t('pr.tr.eyebrow')} title={t('pr.tr.title')} subtitle={t('pr.tr.subtitle')}>
            <JourneyBand steps={TRANSPORT_JOURNEY} t={t} />
            <IndustryPanel icon={Truck} subsector={t('pr.tr.subsector')} serves={t('pr.tr.serves')} groups={TRANSPORT_GROUPS} eligibility={t('pr.tr.elig')} t={t} />
            <MeterRow title={t('pr.tr.meters')} badge={t('pr.tr.badge')} meters={MOVERS_METERS} kind="usd" note={t('pr.tr.metersNote')} t={t} />
          </Section>

          <div className="bg-card/40">
            <Section id="health" eyebrow={t('pr.he.eyebrow')} title={t('pr.he.title')} subtitle={t('pr.he.subtitle')}>
              <JourneyBand steps={HEALTH_JOURNEY} t={t} />
              <IndustryPanel icon={Stethoscope} subsector={t('pr.he.subsector')} serves={t('pr.he.serves')} groups={HEALTH_GROUPS} eligibility={t('pr.he.elig')} t={t} />
              <MeterRow title={t('pr.he.meters')} badge={t('pr.he.badge')} meters={CLINICAL_METERS} kind="credit" note={t('pr.he.metersNote')} t={t} />
            </Section>
          </div>

          <Section id="compare" eyebrow={t('pr.cmp.eyebrow')} title={t('pr.cmp.title')} subtitle={t('pr.cmp.subtitle')}>
            <Legend t={t} />
            <Comparison plans={ladder} featureLabels={data.feature_labels} t={t} />
          </Section>

          <div className="bg-card/40">
            <Section id="addons" eyebrow={t('pr.add.eyebrow')} title={t('pr.add.title')} subtitle={t('pr.add.subtitle')}>
              <AddonCatalog recurring={recurringAddOns} usage={usagePacks} industry={industryAddOns} go={go} t={t} />
            </Section>
          </div>

          <Section id="estimator" eyebrow={t('pr.est.eyebrow')} title={t('pr.est.title')} subtitle={t('pr.est.subtitle')}>
            <Estimator plans={ladder} recurringAddOns={recurringAddOns} usagePacks={usagePacks} navigate={navigate} t={t} />
          </Section>

          <div className="bg-card/40">
            <Section id="fit" eyebrow={t('pr.fit.eyebrow')} title={t('pr.fit.title')} subtitle={t('pr.fit.subtitle')}>
              <div className="grid gap-5 md:grid-cols-3">
                {PLAN_FIT.map((f) => {
                  const plan = plans.find((p) => p.plan_id === f.plan);
                  return (
                    <div key={f.whoKey} className="rounded-xl bg-card border border-border p-6 text-center shadow-[0_10px_30px_-22px_rgba(0,0,0,0.3)]">
                      <div className="text-sm text-muted-foreground font-semibold">{t(f.whoKey)}</div>
                      <div className={`text-2xl font-extrabold my-1.5 ${GRAD_TEXT}`}>{plan?.display_name || f.plan}</div>
                      <div className="text-sm text-muted-foreground">{plan && (plan.price_usd != null ? money(plan.price_usd) + t('pr.est.perMo') : t('pr.card.custom'))}</div>
                      <ul className="text-left text-sm text-muted-foreground mt-3 space-y-1.5">
                        {t(f.whyKey).split('|').map((w) => <li key={w} className="flex gap-2"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" /><span>{w}</span></li>)}
                      </ul>
                      <Button variant="outline" className="mt-5 h-9" onClick={() => go('plans')}>{t('pr.fit.view', { name: plan?.display_name || f.plan })}</Button>
                    </div>
                  );
                })}
              </div>
            </Section>
          </div>

          <Section id="faq" eyebrow={t('pr.faq.eyebrow')} title={t('pr.faq.title')}>
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-2.5">
                {FAQ_KEYS.map(([q, a], i) => (
                  <AccordionItem key={i} value={`q${i}`} className="rounded-xl bg-card border border-border px-4">
                    <AccordionTrigger className="text-left text-[15px] font-semibold hover:no-underline">{t(q)}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm">{t(a)}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Section>

          <section className="mx-auto max-w-6xl px-5 pb-6">
            <div className={`rounded-3xl ${GRAD} text-white text-center px-6 py-14`}>
              <h2 className="text-3xl font-extrabold mb-2.5">{t('pr.cta.title')}</h2>
              <p className="opacity-95 text-lg mb-6">{t('pr.cta.subtitle')}</p>
              <div className="flex gap-3 justify-center flex-wrap">
                <Button className="h-11 px-6 bg-white text-brand-deep hover:bg-white/90" onClick={() => navigate('/signup')}>{t('pr.cta.create')}</Button>
                <a href={`mailto:${SALES_EMAIL}`}><Button variant="outline" className="h-11 px-6 bg-white/10 border-white/40 text-white hover:bg-white/20">{t('pr.cta.contact')}</Button></a>
                <Button variant="outline" className="h-11 px-6 bg-white/10 border-white/40 text-white hover:bg-white/20" onClick={() => navigate('/login')}>{t('pr.cta.back')}</Button>
              </div>
            </div>
          </section>
        </>
      )}

      <footer className="border-t border-border mt-4">
        <div className="mx-auto max-w-6xl px-5 py-7 flex flex-wrap justify-between items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-3"><ServisfyLogo variant="full" size="xl" /><span>{t('pr.footer.tagline')}</span></div>
          <div className="flex gap-5">
            <button className="hover:text-primary" onClick={() => navigate('/privacy-policy')}>{t('pr.footer.privacy')}</button>
            <button className="hover:text-primary" onClick={() => navigate('/terms-and-conditions')}>{t('pr.footer.terms')}</button>
            <button className="hover:text-primary" onClick={() => navigate('/login')}>{t('pr.footer.login')}</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============================ SUBCOMPONENTS ============================ */
function Section({ id, eyebrow, title, subtitle, children }) {
  return (
    <section id={id} className="py-14 scroll-mt-24">
      <div className="mx-auto max-w-6xl px-5">
        <div className="text-center max-w-2xl mx-auto mb-9">
          <div className="text-xs font-bold tracking-widest uppercase text-primary">{eyebrow}</div>
          <h2 className="text-3xl sm:text-[34px] font-extrabold tracking-tight mt-2 mb-2.5 text-foreground">{title}</h2>
          {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}
function SectionState({ children }) { return <div className="mx-auto max-w-6xl px-5 py-20 text-center text-muted-foreground">{children}</div>; }

function JourneyBand({ steps, t }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-4 mb-6 overflow-x-auto shadow-[0_10px_30px_-24px_rgba(0,0,0,0.3)]">
      <div className="flex items-center gap-2 min-w-max">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <span className={`rounded-full px-3 py-1.5 text-sm font-bold ${i === steps.length - 1 ? 'bg-success/20 text-success-foreground' : 'bg-accent text-brand-deep'}`}>{t(s)}</span>
            {i < steps.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function PlanCard({ p, navigate, t }) {
  const isTrial = p.is_trial || p.price_usd === 0;
  const isEnterprise = p.contact_pricing;
  const rec = p.plan_id === 'professional';
  const L = p.limits || {};
  const unlimited = t('pr.val.unlimited');
  const topFeatures = (p.features || [])
    .filter((f) => !['ticketing', 'customer_management', 'staff_management', 'solution_bank'].includes(f) || p.plan_id === 'starter')
    .slice(0, 6).map((f) => flabel(f, t));
  return (
    <div className={`relative flex flex-col rounded-2xl p-6 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.25)] border ${rec ? 'border-transparent bg-card' : 'border-border bg-card'} ${isTrial ? 'bg-success/10' : ''}`}
      style={rec ? { backgroundImage: 'linear-gradient(hsl(var(--card)),hsl(var(--card))), linear-gradient(135deg,#1F5555,#377272,#6FAFAF)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', borderWidth: 2 } : {}}>
      {rec && <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${GRAD} text-primary-foreground text-[11px] font-bold tracking-wide px-3 py-1 rounded-full`}>{t('pr.card.recommended')}</div>}
      <h3 className="text-lg font-bold text-foreground">{p.display_name}</h3>
      <div className="text-sm text-muted-foreground min-h-[34px] mb-3">{t('pr.pos.' + p.plan_id)}</div>
      {isEnterprise ? (
        <div className="mb-4"><span className={`text-4xl font-extrabold ${GRAD_TEXT}`}>{t('pr.card.custom')}</span><div className="text-xs text-muted-foreground mt-1">{t('pr.card.quote')}</div></div>
      ) : (
        <div className="mb-4">
          <span className="text-4xl font-extrabold tracking-tight text-foreground">${p.price_usd}</span>
          <span className="text-sm text-muted-foreground font-semibold"> {isTrial ? t('pr.card.perTrial', { days: p.trial_days || 14 }) : t('pr.card.perMonth')}</span>
          <div className="text-xs text-muted-foreground mt-1">{isTrial ? t('pr.card.noCard') : t('pr.card.billed')}</div>
        </div>
      )}
      <Button className={`w-full justify-center mb-5 ${rec ? `text-primary-foreground ${GRAD} ${GRAD_HOVER}` : ''}`} variant={rec ? 'default' : 'outline'}
        onClick={() => (isEnterprise ? (window.location.href = `mailto:${SALES_EMAIL}`) : navigate(`/signup?plan=${encodeURIComponent(p.plan_id)}`))}>
        {isEnterprise ? t('pr.card.contact') : isTrial ? t('pr.card.startTrial') : t('pr.card.choose', { name: p.display_name })}
      </Button>
      {isTrial ? (
        <ul className="space-y-2.5 text-sm">
          <AllowLi>{t('pr.allow.trialFull')}</AllowLi><AllowLi>{t('pr.allow.trialEval', { days: p.trial_days || 14 })}</AllowLi>
          <AllowLi>{t('pr.allow.trialNoCard')}</AllowLi><AllowLi>{t('pr.allow.trialConvert')}</AllowLi>
        </ul>
      ) : (
        <ul className="space-y-2.5 text-sm">
          <AllowLi>{t('pr.allow.staff', { n: lim(L.max_staff, unlimited) })}</AllowLi>
          <AllowLi>{t('pr.allow.engagements', { n: lim(L.monthly_engagements, unlimited) })}</AllowLi>
          <AllowLi>{t('pr.allow.ai', { n: lim(L.monthly_ai_resolutions, unlimited) })}</AllowLi>
          <AllowLi>{t('pr.allow.storage', { s: lim(L.storage_gb, unlimited), i: lim(L.active_integrations, unlimited) })}</AllowLi>
          <li className="h-px bg-border my-1" />
          {topFeatures.length ? topFeatures.map((f) => (
            <li key={f} className="flex gap-2.5 text-foreground/80"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" /><span>{f}</span></li>
          )) : <li className="text-muted-foreground text-xs">{t('pr.allow.core')}</li>}
        </ul>
      )}
    </div>
  );
}
const AllowLi = ({ children }) => (<li className="flex gap-2.5 font-semibold text-foreground"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" /><span>{children}</span></li>);

function Legend({ t }) {
  const items = [[t('pr.cmp.included'), 'bg-success/15 text-success-foreground'], [t('pr.cmp.notIncluded'), 'bg-muted text-muted-foreground'],
    [t('pr.cmp.addon'), 'bg-primary/12 text-primary'], [t('pr.cmp.usage'), 'bg-info/15 text-info'], [t('pr.cmp.industry'), 'bg-brand-aqua/30 text-brand-deep']];
  return <div className="flex gap-3.5 justify-center flex-wrap mb-6 text-xs text-muted-foreground">{items.map(([l, c]) => <span key={l} className={`inline-flex items-center rounded px-2 py-0.5 font-bold ${c}`}>{l}</span>)}</div>;
}
const T = {
  inc: <span className="inline-block rounded px-2 py-0.5 text-[11px] font-bold bg-success/15 text-success-foreground">✓</span>,
  no: <span className="inline-block text-muted-foreground/60 font-bold">—</span>,
  add: <span className="inline-block rounded px-2 py-0.5 text-[11px] font-bold bg-primary/12 text-primary">+</span>,
  usage: <span className="inline-block rounded px-2 py-0.5 text-[11px] font-bold bg-info/15 text-info">~</span>,
  ind: <span className="inline-block rounded px-2 py-0.5 text-[11px] font-bold bg-brand-aqua/30 text-brand-deep">◆</span>,
};
function cellFor(row, p, unlimited) {
  if (typeof row === 'object') {
    if (row.limit) return <span className="font-bold">{lim((p.limits || {})[row.limit], unlimited)}</span>;
    if (row.tag === 'add') return T.add; if (row.tag === 'usage') return T.usage; if (row.tag === 'ind') return T.ind;
  }
  return (p.features || []).includes(row) ? T.inc : T.no;
}
function rowLabel(row, featureLabels, t) {
  if (typeof row === 'object') return row.labelKey ? t(row.labelKey) : (row.label || llabel(row.limit, t));
  // Prefer the translated feature label; fall back to the server catalogue name, then English metadata.
  const k = 'pr.feat.' + row; const v = t(k);
  return v === k ? ((featureLabels && featureLabels[row]) || FEATURE_LABEL[row] || row) : v;
}
function Comparison({ plans, featureLabels, t }) {
  const [sel, setSel] = useState(plans.find((p) => p.plan_id === 'professional')?.plan_id || plans[0]?.plan_id);
  const unlimited = t('pr.val.unlimited');
  return (
    <>
      <div className="hidden md:block rounded-xl bg-card border border-border shadow-[0_10px_30px_-20px_rgba(0,0,0,0.2)]">
        <table className="w-full text-sm border-collapse">
          <caption className="sr-only">{t('pr.cmp.title')}</caption>
          {/* Opaque sticky header (bg-secondary, NOT /80) so scrolled rows never bleed through;
              z-30 keeps it above the group-section rows; top-16 clears the 64px sticky nav. */}
          <thead><tr>
            <th scope="col" className="text-left px-3.5 py-3 bg-secondary font-bold text-foreground sticky top-20 z-30 border-b-2 border-border shadow-[0_6px_10px_-8px_rgba(0,0,0,0.35)]">{t('pr.nav.compare')}</th>
            {plans.map((p) => <th key={p.plan_id} scope="col" className={`px-3.5 py-3 bg-secondary font-bold sticky top-20 z-30 border-b-2 border-border shadow-[0_6px_10px_-8px_rgba(0,0,0,0.35)] ${p.plan_id === 'professional' ? 'text-primary' : 'text-foreground'}`}>{p.display_name}</th>)}
          </tr></thead>
          <tbody>
            {COMPARE_GROUPS.map((G) => (
              <React.Fragment key={G.gKey}>
                <tr><th scope="colgroup" colSpan={plans.length + 1} className="bg-muted text-left px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground border-y border-border">{t(G.gKey)}</th></tr>
                {G.rows.map((row, i) => (
                  <tr key={i} className="border-t border-border/70 hover:bg-accent/30">
                    <th scope="row" className="text-left px-3.5 py-2 font-medium text-foreground/90">{rowLabel(row, featureLabels, t)}</th>
                    {plans.map((p) => <td key={p.plan_id} className="px-3.5 py-2 text-center align-middle">{cellFor(row, p, unlimited)}</td>)}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden">
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1">
          {plans.map((p) => (
            <button key={p.plan_id} onClick={() => setSel(p.plan_id)} className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold border ${sel === p.plan_id ? `text-primary-foreground ${GRAD} border-transparent` : 'bg-card border-border text-muted-foreground'}`}>{p.display_name}</button>
          ))}
        </div>
        {(() => {
          const p = plans.find((x) => x.plan_id === sel) || plans[0];
          if (!p) return null;
          return (
            <div className="rounded-xl bg-card border border-border p-4 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.2)]">
              <div className="flex items-baseline justify-between mb-3"><div className="font-bold text-lg">{p.display_name}</div><div className="font-extrabold">{p.contact_pricing ? t('pr.card.custom') : `$${p.price_usd}${t('pr.est.perMo')}`}</div></div>
              {COMPARE_GROUPS.map((G) => (
                <div key={G.gKey} className="mb-3">
                  <div className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground mb-1.5">{t(G.gKey)}</div>
                  <ul className="space-y-1.5">
                    {G.rows.map((row, i) => (
                      <li key={i} className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{rowLabel(row, featureLabels, t)}</span><span className="flex items-center gap-1">{cellFor(row, p, unlimited)}</span></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </>
  );
}

function IndustryPanel({ icon: Icon, subsector, serves, groups, eligibility, t }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-6 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.2)]">
      <div className="flex items-start gap-4 mb-4">
        <div className={`h-12 w-12 rounded-xl ${GRAD} text-primary-foreground flex items-center justify-center shrink-0`}><Icon className="h-6 w-6" /></div>
        <div><div className="text-sm text-muted-foreground mt-0.5">{subsector}</div></div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{serves}</p>
      <div className="grid gap-4 md:grid-cols-3">
        {groups.map((g) => (
          <div key={g.hKey} className="rounded-xl border border-border p-4">
            <div className="text-sm font-semibold mb-2 text-foreground">{t(g.hKey)}</div>
            <ul className="space-y-1.5">{t(g.itemsKey).split('|').map((it) => <li key={it} className="flex gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" /><span>{it}</span></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="mt-4 text-xs text-muted-foreground bg-muted border border-border rounded-xl p-3">{eligibility}</div>
    </div>
  );
}

function MeterRow({ title, badge, meters, kind, note, t }) {
  return (
    <div className="mt-6">
      <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-foreground">{title}
        <Badge className={kind === 'credit' ? 'bg-brand-aqua/30 text-brand-deep hover:bg-brand-aqua/30' : 'bg-info/15 text-info hover:bg-info/15'}>{badge}</Badge>
      </h3>
      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        {meters.map((m) => (
          <div key={m.key} className="rounded-xl bg-card border border-border p-4 shadow-[0_8px_24px_-20px_rgba(0,0,0,0.2)]">
            <div className="font-semibold text-sm text-foreground">{t(m.nameKey)}</div>
            <div className={`text-xl font-extrabold my-1 ${GRAD_TEXT}`}>{m.rate || t(m.rateKey)}</div>
            <div className="text-[11.5px] text-muted-foreground">{t(m.perKey)}{m.bucketKey ? ` · ${t(m.bucketKey)}` : ''}</div>
            <div className="text-[10.5px] text-muted-foreground mt-1 font-mono">{m.key}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-xs text-muted-foreground bg-muted border border-border rounded-xl p-3">{note}</div>
    </div>
  );
}

function AddonCatalog({ recurring, usage, industry, go, t }) {
  const hasApiData = (recurring && recurring.length) || (usage && usage.length) || (industry && industry.length);
  if (hasApiData) {
    return (
      <>
        <h3 className="text-base font-semibold mb-3 text-foreground">{t('pr.add.recurring')}</h3>
        <AddonGrid items={recurring} unit={t('pr.est.perMo')} none={t('pr.add.none')} />
        <h3 className="text-base font-semibold mt-7 mb-3 text-foreground">{t('pr.add.onetime')}</h3>
        <AddonGrid items={usage} unit={t('pr.est.oneTimeUnit')} none={t('pr.add.none')} />
        {industry && industry.length > 0 && (<><h3 className="text-base font-semibold mt-7 mb-3 text-foreground">{t('pr.add.peruse')}</h3><AddonGrid items={industry} unit="" none={t('pr.add.none')} /></>)}
      </>
    );
  }
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="rounded-2xl bg-card border border-border p-6"><h3 className="font-bold mb-2 text-foreground">{t('pr.add.recurring')}</h3>
        <ul className="text-sm space-y-1.5 text-muted-foreground">{t('pr.add.recurItems').split('|').map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" /><span>{x}</span></li>)}</ul></div>
      <div className="rounded-2xl bg-card border border-border p-6"><h3 className="font-bold mb-2 text-foreground">{t('pr.add.onetime')}</h3>
        <ul className="text-sm space-y-1.5 text-muted-foreground">{t('pr.add.oneItems').split('|').map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" /><span>{x}</span></li>)}</ul>
        <div className="mt-3 text-xs text-muted-foreground">{t('pr.add.oneNote')}</div></div>
      <div className="rounded-2xl bg-card border border-border p-6"><h3 className="font-bold mb-2 text-foreground">{t('pr.add.peruse')}</h3>
        <ul className="text-sm space-y-1.5 text-muted-foreground">
          <li className="flex justify-between"><span>{t('pr.meter.sms')}</span><b className="text-foreground">$0.05{t('pr.est.perMsg')}</b></li>
          <li className="flex justify-between"><span>{t('pr.meter.routes')}</span><b className="text-foreground">$0.01{t('pr.est.perReq')}</b></li>
          <li className="flex justify-between"><span>{t('pr.meter.audio')}</span><b className="text-foreground">1 cr / 5m</b></li>
          <li className="flex justify-between"><span>{t('pr.meter.video')}</span><b className="text-foreground">2 cr / 5m</b></li>
          <li className="flex justify-between"><span>{t('pr.meter.trans')}</span><b className="text-foreground">1 cr / m</b></li>
          <li className="flex justify-between"><span>{t('pr.meter.aidoc')}</span><b className="text-foreground">10 cr</b></li>
        </ul></div>
      <div className="lg:col-span-3 flex flex-wrap items-center gap-2.5">
        <Badge className="bg-brand-aqua/30 text-brand-deep hover:bg-brand-aqua/30">{t('pr.add.moversBadge')}</Badge>
        <Badge className="bg-brand-aqua/30 text-brand-deep hover:bg-brand-aqua/30">{t('pr.add.clinicBadge')}</Badge>
        <Button variant="outline" className="h-9 ml-auto" onClick={() => go('estimator')}>{t('pr.add.tryEst')}</Button>
      </div>
    </div>
  );
}
function AddonGrid({ items, unit, none }) {
  if (!items || !items.length) return <p className="text-sm text-muted-foreground">{none}</p>;
  return (
    <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((a) => (
        <div key={a.id} className="rounded-xl bg-card border border-border p-4 shadow-[0_8px_24px_-20px_rgba(0,0,0,0.2)]">
          <div className="font-semibold text-sm">{a.name}</div>
          <div className={`text-xl font-extrabold my-1 ${GRAD_TEXT}`}>{a.price_usd == null ? '—' : '$' + a.price_usd}</div>
          <div className="text-[11.5px] text-muted-foreground">{unit}</div>
        </div>
      ))}
    </div>
  );
}

function Estimator({ plans, recurringAddOns, usagePacks, navigate, t }) {
  const [planId, setPlanId] = useState(plans.find((p) => p.plan_id === 'professional')?.plan_id || plans[0]?.plan_id);
  const [addQ, setAddQ] = useState({});
  const [packQ, setPackQ] = useState({});
  const [use, setUse] = useState({ sms: 0, routes: 0, audioMin: 0, videoMin: 0, transMin: 0, aiDocs: 0 });
  const plan = plans.find((p) => p.plan_id === planId) || plans[0];
  const isEnt = plan?.contact_pricing;
  const bump = (set, id, d) => set((s) => ({ ...s, [id]: Math.max(0, (s[id] || 0) + d) }));
  const bumpUse = (id, d) => setUse((s) => ({ ...s, [id]: Math.max(0, (s[id] || 0) + d) }));
  const reset = () => { setAddQ({}); setPackQ({}); setUse({ sms: 0, routes: 0, audioMin: 0, videoMin: 0, transMin: 0, aiDocs: 0 }); };
  const payableAddOns = recurringAddOns.filter((a) => a.price_usd != null);
  const addSum = payableAddOns.reduce((s, a) => s + (addQ[a.id] || 0) * a.price_usd, 0);
  const base = isEnt ? 0 : (plan?.price_usd || 0);
  const usdUsage = use.sms * 0.05 + use.routes * 0.01;
  const commCredits = Math.ceil(use.audioMin / 5) * 1 + Math.ceil(use.videoMin / 5) * 2;
  const aiOpsCredits = use.transMin * 1 + use.aiDocs * 10;
  const oneTime = usagePacks.reduce((s, a) => s + (packQ[a.id] || 0) * (a.price_usd || 0), 0);

  return (
    <div className="grid gap-5 lg:grid-cols-[1.3fr_0.9fr] items-start">
      <div className="rounded-2xl bg-card border border-border p-6 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.2)]">
        <label htmlFor="est-plan-select" className="block text-sm font-bold mb-2 text-foreground">{t('pr.est.planLabel')}</label>
        <select id="est-plan-select" aria-label={t('pr.est.planLabel')} value={planId} onChange={(e) => setPlanId(e.target.value)} className="w-full h-11 border border-border rounded-xl px-3 bg-card text-foreground">
          {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.display_name}{p.price_usd != null ? ` — $${p.price_usd}${t('pr.est.perMo')}` : ' — ' + t('pr.card.custom')}</option>)}
        </select>
        {isEnt ? (
          <div className="mt-4 text-sm font-semibold text-warning-foreground bg-warning/10 border border-dashed border-warning/40 rounded-xl p-3">{t('pr.est.entNote')}</div>
        ) : (
          <>
            {payableAddOns.length > 0 ? (
              <><div className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground mt-5 mb-2">{t('pr.est.recurLabel')}</div>
                {payableAddOns.map((a) => <QtyRow key={a.id} name={a.name} price={a.price_usd} unit={t('pr.est.perMo')} q={addQ[a.id] || 0} onDec={() => bump(setAddQ, a.id, -1)} onInc={() => bump(setAddQ, a.id, 1)} />)}</>
            ) : (<div className="mt-5 text-xs text-muted-foreground bg-muted border border-border rounded-xl p-3">{t('pr.est.recurNote')}</div>)}
            {usagePacks.length > 0 && (<><div className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground mt-5 mb-2">{t('pr.est.oneLabel')}</div>
              {usagePacks.map((a) => <QtyRow key={a.id} name={a.name} price={a.price_usd} unit={t('pr.est.oneTimeUnit')} q={packQ[a.id] || 0} onDec={() => bump(setPackQ, a.id, -1)} onInc={() => bump(setPackQ, a.id, 1)} />)}</>)}
            <div className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground mt-6 mb-1">{t('pr.est.meterLabel')}</div>
            <div className="text-[11px] text-muted-foreground mb-2">{t('pr.est.moversHint')}</div>
            <QtyRow name={t('pr.est.smsRow')} price={0.05} unit={t('pr.est.perMsg')} q={use.sms} onDec={() => bumpUse('sms', -1)} onInc={() => bumpUse('sms', 1)} />
            <QtyRow name={t('pr.est.routesRow')} price={0.01} unit={t('pr.est.perReq')} q={use.routes} onDec={() => bumpUse('routes', -1)} onInc={() => bumpUse('routes', 1)} />
            <div className="text-[11px] text-muted-foreground mt-3 mb-2">{t('pr.est.clinicHint')}</div>
            <QtyRow name={t('pr.est.audioRow')} price="1 cr / 5m" q={use.audioMin} onDec={() => bumpUse('audioMin', -5)} onInc={() => bumpUse('audioMin', 5)} />
            <QtyRow name={t('pr.est.videoRow')} price="2 cr / 5m" q={use.videoMin} onDec={() => bumpUse('videoMin', -5)} onInc={() => bumpUse('videoMin', 5)} />
            <QtyRow name={t('pr.est.transRow')} price="1 cr / m" q={use.transMin} onDec={() => bumpUse('transMin', -1)} onInc={() => bumpUse('transMin', 1)} />
            <QtyRow name={t('pr.est.aidocRow')} price="10 cr" q={use.aiDocs} onDec={() => bumpUse('aiDocs', -1)} onInc={() => bumpUse('aiDocs', 1)} />
            <Button variant="outline" className="h-9 mt-4" onClick={reset}><RotateCcw className="h-4 w-4 mr-1.5" /> {t('pr.est.reset')}</Button>
          </>
        )}
      </div>
      <div className="rounded-2xl bg-card border border-border p-6 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.2)] lg:sticky lg:top-24">
        <div className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground mb-2">{t('pr.est.yourEst')}</div>
        <Row l={`${plan?.display_name || ''}`} r={isEnt ? t('pr.est.contactSales') : money(base)} />
        <Row l={t('pr.est.recurInEst')} r={money(addSum)} />
        <div className="flex justify-between text-[17px] font-extrabold border-t-2 border-border mt-2 pt-3"><span>{t('pr.est.monthlyTotal')}</span><span>{isEnt ? t('pr.est.contactSales') : money(base + addSum)}</span></div>
        <div className="text-xs text-muted-foreground mt-0.5">{t('pr.est.monthlyNote')}</div>
        {!isEnt && (
          <>
            <div className="bg-info/10 border border-dashed border-info/40 rounded-xl p-3 mt-3">
              <div className="flex justify-between text-sm"><span>{t('pr.est.oneToday')}</span><span className="font-semibold">{money(oneTime)}</span></div>
              <div className="flex justify-between text-sm"><span>{t('pr.est.usdUsage')}</span><span className="font-semibold">{money(usdUsage)}</span></div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('pr.est.usageNote')}</div>
            </div>
            <div className="bg-brand-aqua/15 border border-dashed border-brand-aqua/50 rounded-xl p-3 mt-3">
              <div className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground mb-1">{t('pr.est.creditTitle')}</div>
              <div className="flex justify-between text-sm"><span>{t('pr.est.commCredits')}</span><span className="font-semibold">{commCredits.toLocaleString('en-US')}</span></div>
              <div className="flex justify-between text-sm"><span>{t('pr.est.aiCredits')}</span><span className="font-semibold">{aiOpsCredits.toLocaleString('en-US')}</span></div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('pr.est.creditNote')}</div>
            </div>
          </>
        )}
        <Button className={`w-full justify-center mt-4 text-primary-foreground ${GRAD} ${GRAD_HOVER}`} onClick={() => (isEnt ? (window.location.href = `mailto:${SALES_EMAIL}`) : navigate(`/signup?plan=${encodeURIComponent(plan.plan_id)}`))}>
          {isEnt ? t('pr.est.contactSales') : t('pr.est.start')}
        </Button>
      </div>
    </div>
  );
}
const Row = ({ l, r }) => <div className="flex justify-between text-sm py-1.5 text-muted-foreground"><span>{l}</span><span>{r}</span></div>;
function QtyRow({ name, price, unit, q, onDec, onInc }) {
  const priceText = typeof price === 'number' ? `${money(price)} ${unit || ''}` : price;
  return (
    <div className="flex items-center justify-between gap-2.5 py-2.5 border-b border-dashed border-border">
      <div className="text-sm text-foreground">{name}<small className="block text-muted-foreground text-[11.5px]">{priceText}</small></div>
      <div className="flex items-center gap-1.5">
        <button className="w-7 h-7 border border-border rounded-lg bg-card" onClick={onDec} aria-label={`- ${name}`}><Minus className="h-3.5 w-3.5 mx-auto" /></button>
        <span className="w-9 text-center text-sm font-semibold">{q}</span>
        <button className="w-7 h-7 border border-border rounded-lg bg-card" onClick={onInc} aria-label={`+ ${name}`}><Plus className="h-3.5 w-3.5 mx-auto" /></button>
      </div>
    </div>
  );
}
